// Resolves the spreadsheet's free-text equipment-type column to one of the
// organisation's opted-in equipment types. Resources store the *org selection
// row id* (`organisationEquipmentTypeId`), not the catalogue key, so the
// importer needs a text → id lookup scoped to the right equipment class.

import type {
  EquipmentCatalogue,
  EquipmentClassValue,
  OrganisationEquipmentType,
} from '@my/directory-api';
import type { ImportResourceType } from './types';

/** One selectable equipment type for the chosen resource type. */
export interface EquipmentOption {
  id: string;
  label: string;
  catalogueKey: string;
}

const normalise = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Equipment class a resource type takes its equipment types from. */
export function equipmentClassFor(type: ImportResourceType): EquipmentClassValue | null {
  if (type === 'Trailer') return 'Trailer';
  if (type === 'Rigid') return 'Rigid';
  return null;
}

/**
 * The org's equipment types for a resource type, labelled from the catalogue
 * (falling back to the raw catalogue key when the catalogue has no entry).
 */
export function buildEquipmentOptions(
  rows: OrganisationEquipmentType[],
  labelByKey: Map<string, string>,
  type: ImportResourceType,
): EquipmentOption[] {
  const expected = equipmentClassFor(type);
  if (expected === null) return [];
  return rows
    .filter((r) => r.class === expected)
    .map((r) => ({
      id: r.id,
      label: labelByKey.get(r.catalogueKey) ?? r.catalogueKey,
      catalogueKey: r.catalogueKey,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Lookup index for {@link resolveEquipmentId} — every option indexed by its
 * normalised label, catalogue key and the key's last segment, so a file saying
 * "REFRIGERATED", "Refrigerated" or "trailer.reefer.refrigerated" all land.
 * Ambiguous aliases (two options claiming the same alias) are dropped rather
 * than guessed.
 */
export function buildEquipmentIndex(options: EquipmentOption[]): Map<string, string> {
  const claims = new Map<string, Set<string>>();
  const claim = (alias: string, id: string) => {
    if (!alias) return;
    const set = claims.get(alias) ?? new Set<string>();
    set.add(id);
    claims.set(alias, set);
  };

  for (const o of options) {
    claim(normalise(o.label), o.id);
    claim(normalise(o.catalogueKey), o.id);
    const lastSegment = o.catalogueKey.split('.').pop() ?? '';
    claim(normalise(lastSegment), o.id);
  }

  const index = new Map<string, string>();
  for (const [alias, ids] of claims) {
    if (ids.size === 1) index.set(alias, [...ids][0]!);
  }
  return index;
}

/**
 * Resolve free-text to an equipment-type id. Exact (normalised) match first,
 * then a containment pass that only commits when exactly one option matches.
 * Returns '' when nothing matches — the row still imports, flagged with a
 * warning, per the importer's "match existing, warn on unknown" rule.
 */
export function resolveEquipmentId(
  text: string,
  index: Map<string, string>,
  options: EquipmentOption[],
): string {
  const needle = normalise(text);
  if (!needle) return '';

  const exact = index.get(needle);
  if (exact) return exact;

  const partial = options.filter((o) => {
    const label = normalise(o.label);
    return label.includes(needle) || needle.includes(label);
  });
  return partial.length === 1 ? partial[0]!.id : '';
}

/** Flatten the equipment catalogue into catalogue key → display label. */
export function buildEquipmentLabelIndex(
  catalogue: EquipmentCatalogue | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const cls of catalogue?.classes ?? []) {
    for (const group of cls.groups) {
      for (const type of group.types) map.set(type.key, type.label);
    }
  }
  return map;
}
